import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import AnimeCard from "@/components/base/AnimeCard";
import NewsCard from "@/components/base/NewsCard";
import CharacterCard from "@/components/base/CharacterCard";
import SpecialCard from "@/components/base/SpecialCard";
import {
  getSpecialById,
  getAnimeById,
  getNewsById,
  getCharacterById,
} from "@/utils/content";
import { specialList } from "@/mocks/specials";

export default function SpecialPage() {
  const { id = "" } = useParams();
  const special = getSpecialById(id);

  usePageMeta({
    title: special ? `${special.title} - 风车动漫网` : "专题策划 - 风车动漫网",
    description: special
      ? special.intro
      : "风车动漫网专题策划，围绕主题把动画、新闻与角色串成合集，方便按兴趣系统浏览内容。",
    keywords: "动漫专题,专题策划,动画合集,风车动漫网",
  });

  if (!special) {
    return (
      <Layout>
        <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="专题不存在或已下线"
            description="该专题可能已被移除，或链接有误。返回首页看看其他精选专题吧。"
            action={
              <Link
                to="/"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页
              </Link>
            }
          />
        </div>
      </Layout>
    );
  }

  const animeItems = special.animeIds.map((item) => getAnimeById(item)).filter(Boolean);
  const newsItems = special.newsIds.map((item) => getNewsById(item)).filter(Boolean);
  const characterItems = special.characterIds
    .map((item) => getCharacterById(item))
    .filter(Boolean);
  const relatedSpecials = specialList.filter((item) => item.id !== special.id).slice(0, 3);

  return (
    <Layout>
      <section className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6">
        <Breadcrumb items={[{ label: "专题策划", to: "/" }, { label: special.title }]} />
      </section>

      <section className="mx-auto mt-5 max-w-[1280px] px-4 md:px-6">
        <div className="relative overflow-hidden rounded-lg bg-foreground-950">
          <img
            src={special.cover}
            alt={`专题《${special.title}》主视觉`}
            title={`${special.title} 专题策划`}
            className="h-[280px] w-full object-cover object-top md:h-[380px]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10"></div>
          <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10">
            <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-bookmark-3-line"></i>
              专题策划
            </span>
            <h1 className="text-2xl font-bold text-background-50 md:text-4xl">{special.title}</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-100/85 md:text-base">
              {special.intro}
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
        <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-6">
          <h2 className="mb-2 flex items-center gap-2 text-base font-bold text-secondary-900">
            <i className="ri-lightbulb-flash-line"></i>
            推荐理由
          </h2>
          <p className="text-sm leading-relaxed text-secondary-900/85">{special.reason}</p>
        </div>

        {animeItems.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-5 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              动画合集
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {animeItems.map(
                (anime) => anime && <AnimeCard key={anime.id} anime={anime} />,
              )}
            </div>
          </section>
        )}

        {newsItems.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-5 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500"></span>
              新闻集锦
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
              {newsItems.map((news) => news && <NewsCard key={news.id} news={news} />)}
            </div>
          </section>
        )}

        {characterItems.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-5 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-secondary-500"></span>
              角色专题
            </h2>
            <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
              {characterItems.map(
                (character) =>
                  character && <CharacterCard key={character.id} character={character} />,
              )}
            </div>
          </section>
        )}

        {relatedSpecials.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-5 text-lg font-bold text-foreground-950">相关专题推荐</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {relatedSpecials.map((item) => (
                <SpecialCard key={item.id} special={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}